import AuthorizationError from '@/common/utils/errors/AuthorizationError';
import isSelfService from '@/common/utils/isSelfService';
import RequestController from '@/common/utils/requestController';
import { OPENSTACK_AUTH_RECEIPT_HEADER } from '@/common/constants/auth.constants';
import { HTTP_STATUS_UNAUTHORIZED } from '@/common/constants/httpStatus.constants';
import { AxiosHeaderValue, AxiosRequestConfig, AxiosResponse } from 'axios';

export interface StoreAdapter {
  getScopedToken: () => string;
  unauthorize: () => Promise<void>;
  checkAccessDisabled: () => Promise<void>;
  checkReconfiguration: () => Promise<void>;
}

export interface HttpRequestOptions extends AxiosRequestConfig {
  unscopedRequest?: boolean;
  skipAuthGuard?: boolean;
  forceAuthGuard?: boolean;
  abortController?: AbortController;
}

export interface AuthGuardParams {
  skipAuthGuard?: boolean;
  forceAuthGuard?: boolean;
}

export default abstract class BaseHttpAdapter extends RequestController {
  readonly defaultHeaders: FetchHeaders = {
    Accept: 'application/json, text/plain, */*',
    'X-Requested-With': 'XMLHttpRequest'
  };

  storeAdapter?: StoreAdapter;

  setAdapter(storeAdapter: StoreAdapter): void {
    this.storeAdapter = storeAdapter;
  }

  getAuthorizationHeaders(options: { unscopedRequest?: boolean }): XAuthorizationHeaders {
    const scopedToken = this.storeAdapter?.getScopedToken();
    return {
      'X-Session-Id': isSelfService() ? 1 : 0,
      ...(!options.unscopedRequest
        && scopedToken
        && { [this.scopedTokenHeaderName]: scopedToken }
      )
    };
  }

  async unauthorize(): Promise<void> {
    return this.storeAdapter?.unauthorize();
  }

  protected getHeader(headers: Response['headers'] | AxiosResponse['headers'], name: string): AxiosHeaderValue | string | null {
    if (typeof headers.get === 'function') {
      return headers.get(name);
    }
    return headers[name] ?? null;
  }

  // @cpt-algo:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1
  async authGuard<T extends Response | AxiosResponse>(
    response: T,
    data: ErrorBody | undefined,
    {
      skipAuthGuard,
      forceAuthGuard
    }: AuthGuardParams
  ): Promise<T> {
    if (response.status !== HTTP_STATUS_UNAUTHORIZED) {
      return response;
    }

    if (!isSelfService()) {
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-non-self-service
      await this.unauthorize();
      throw new AuthorizationError(response, data);
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-non-self-service
    }

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-skip
    if (skipAuthGuard) {
      return response;
    }
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-skip

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-force
    if (forceAuthGuard) {
      await this.unauthorize();
      throw new AuthorizationError(response, data);
    }
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-force

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-read-totp
    const totpReceipt = this.getHeader(response.headers, OPENSTACK_AUTH_RECEIPT_HEADER);
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-read-totp
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-totp-throw
    if (totpReceipt) {
      throw new AuthorizationError(response, data);
    }
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-totp-throw

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-check-access
    try {
      await this.storeAdapter?.checkAccessDisabled();
    } catch (e) {
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-check-access
      // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-access-disabled-throw
      if (e instanceof AuthorizationError) {
        await this.unauthorize();
        throw new AuthorizationError(response, data);
      }
      // @cpt-end:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-access-disabled-throw
    }

    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-pass-through
    return response;
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-auth-guard:p1:inst-pass-through
  }

  // @cpt-algo:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1
  async reconfigurationHook<T extends Response | AxiosResponse>(response: T): Promise<T> {
    const errorPathName = '/service-unavailable';
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-read-header
    const reconfigurationState = this.getHeader(response.headers, 'X-Reconfiguration-State');
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-read-header
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-has-reconfig
    if (reconfigurationState) {
      if (isSelfService()) {
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-redirect
        if (window.location.pathname !== errorPathName) {
          window.location.pathname = errorPathName;
        }
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-redirect
      } else {
        // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-check-reconfig
        await this.storeAdapter?.checkReconfiguration();
        // @cpt-end:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-check-reconfig
      }
    }
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-has-reconfig
    // @cpt-begin:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-return
    return response;
    // @cpt-end:cpt-pstorageuihci-algo-create-fetch-reconfiguration:p1:inst-return
  }

  abstract get<T = unknown>(
    path: UriPath,
    params?: FetchRequestParams,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;

  abstract post<T = unknown>(
    path: UriPath,
    body?: FetchRequestBody,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;

  abstract put<T = unknown>(
    path: UriPath,
    body?: FetchRequestBody,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;

  abstract patch<T = unknown>(
    path: UriPath,
    body?: FetchRequestBody,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;

  abstract delete<T = unknown>(
    path: UriPath,
    body?: FetchRequestBody,
    options?: HttpRequestOptions
  ): Promise<FetchResponse<T>>;
}
